import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { getUserBookmarks, addToBookmarks, removeFromBookmarksByImageRelation } from '../api/bookmarks';
import { getCacheVersions } from '../api/cache';
import { cacheStore } from '../utils/cacheStore';
import { useAuth } from './AuthContext';
import { useAuthStatus } from '../hooks/useAuthStatus';

const BookmarksContext = createContext(null);

const MAX_BOOKMARKS_TO_CACHE = 1000;
const CACHE_KEY = 'bookmarks';

function sortBookmarks(array) {
	return array.sort((a, b) => {
		if (a.created_at && b.created_at) {
			return new Date(b.created_at) - new Date(a.created_at);
		}
		if (a.id && b.id) return b.id - a.id;
		return 0;
	});
}

export const BookmarksProvider = ({ children }) => {
	const { user } = useAuth();
	const { isReady } = useAuthStatus();
	const [bookmarks, setBookmarks] = useState([]);
	const [bookmarkedIds, setBookmarkedIds] = useState(() => new Set());
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const initializedRef = useRef(false);
	const versionRef = useRef(-1);

	// Load bookmarks: localStorage first (instant), then version check, then server if needed
	useEffect(() => {
		const load = async () => {
			if (!user || !isReady) {
				setBookmarks([]);
				setBookmarkedIds(new Set());
				setLoading(false);
				setError(null);
				initializedRef.current = false;
				return;
			}
			if (initializedRef.current) return;

			// 1. Instant: show cached data from localStorage
			const cached = cacheStore.get(CACHE_KEY);
			if (cached && cached.data) {
				const limited = cached.data.slice(0, MAX_BOOKMARKS_TO_CACHE);
				setBookmarks(limited);
				setBookmarkedIds(new Set(limited.map(b => Number(b.image_relation_id))));
				versionRef.current = cached.version;
				initializedRef.current = true;
			}

			// 2. Check version (lightweight request)
			try {
				const versions = await getCacheVersions();
				const serverVersion = versions?.bookmarks ?? -1;

				if (cached && cached.version === serverVersion) {
					// Cache is valid — no full fetch needed
					setLoading(false);
					return;
				}

				// 3. Version mismatch or no cache — full fetch
				setLoading(true);
				const data = await getUserBookmarks();
				const array = Array.isArray(data) ? data : [];
				const filtered = array
					.filter(b => b && (b.image_relation_id !== undefined && b.image_relation_id !== null));
				const sorted = sortBookmarks(filtered);
				const limited = sorted.slice(0, MAX_BOOKMARKS_TO_CACHE);

				setBookmarks(limited);
				setBookmarkedIds(new Set(limited.map(b => Number(b.image_relation_id))));
				versionRef.current = serverVersion;
				initializedRef.current = true;

				// Save to localStorage with server version
				cacheStore.set(CACHE_KEY, limited, serverVersion);
			} catch (e) {
				setError(e);
				// If we showed cached data, that's still visible — don't clear it
				if (!cached) {
					initializedRef.current = false;
				}
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [user, isReady]);

	const isBookmarked = useMemo(() => {
		return (imageRelationId) => bookmarkedIds.has(Number(imageRelationId));
	}, [bookmarkedIds]);

	const addBookmark = useCallback(async (imageRelationId) => {
		const idNum = Number(imageRelationId);
		if (!user) return false;
		if (bookmarkedIds.has(idNum)) return true;
		// optimistic add
		setBookmarkedIds(prev => new Set(prev).add(idNum));
		try {
			const created = await addToBookmarks(idNum);
			if (created && created.image_relation_id != null) {
				try {
					const fullBookmarkData = await getUserBookmarks();
					const bookmarkData = Array.isArray(fullBookmarkData)
						? fullBookmarkData.find(b => Number(b.image_relation_id) === idNum)
						: null;

					if (bookmarkData) {
						setBookmarks(prev => {
							const existing = prev.find(b => Number(b.image_relation_id) === idNum);
							let updated;
							if (existing) {
								updated = prev.map(b =>
									Number(b.image_relation_id) === idNum ? bookmarkData : b
								);
							} else if (prev.length < MAX_BOOKMARKS_TO_CACHE) {
								updated = [bookmarkData, ...prev];
							} else {
								updated = prev;
							}
							// Increment local version and save cache
							versionRef.current += 1;
							cacheStore.set(CACHE_KEY, updated, versionRef.current);
							return updated;
						});
					}
				} catch (loadError) {
					console.error('Ошибка загрузки полных данных закладки:', loadError);
					setBookmarks(prev => {
						if (prev.some(b => Number(b.image_relation_id) === idNum)) return prev;
						if (prev.length >= MAX_BOOKMARKS_TO_CACHE) return prev;
						const updated = [created, ...prev];
						versionRef.current += 1;
						cacheStore.set(CACHE_KEY, updated, versionRef.current);
						return updated;
					});
				}
			}
			return true;
		} catch (error) {
			if (error?.response?.status === 400 && error?.response?.data?.detail === 'Image already in bookmarks') {
				return true;
			}
			setBookmarkedIds(prev => {
				const next = new Set(prev);
				next.delete(idNum);
				return next;
			});
			return false;
		}
	}, [user, bookmarkedIds]);

	const removeBookmark = useCallback(async (imageRelationId) => {
		const idNum = Number(imageRelationId);
		if (!user) return false;
		if (!bookmarkedIds.has(idNum)) return true;
		// optimistic remove
		setBookmarkedIds(prev => {
			const next = new Set(prev);
			next.delete(idNum);
			return next;
		});
		try {
			await removeFromBookmarksByImageRelation(idNum);
			setBookmarks(prev => {
				const updated = prev.filter(b => Number(b.image_relation_id) !== idNum);
				versionRef.current += 1;
				cacheStore.set(CACHE_KEY, updated, versionRef.current);
				return updated;
			});
			return true;
		} catch (error) {
			setBookmarkedIds(prev => new Set(prev).add(idNum));
			return false;
		}
	}, [user, bookmarkedIds]);

	const toggleBookmark = useCallback(async (imageRelationId) => {
		if (isBookmarked(imageRelationId)) {
			return removeBookmark(imageRelationId);
		} else {
			return addBookmark(imageRelationId);
		}
	}, [isBookmarked, removeBookmark, addBookmark]);

	const reloadBookmarks = useCallback(async () => {
		if (!user) return;
		setLoading(true);
		setError(null);
		try {
			const data = await getUserBookmarks();
			const array = Array.isArray(data) ? data : [];
			const filtered = array
				.filter(b => b && (b.image_relation_id !== undefined && b.image_relation_id !== null));
			const sorted = sortBookmarks(filtered);
			const limited = sorted.slice(0, MAX_BOOKMARKS_TO_CACHE);
			setBookmarks(limited);
			setBookmarkedIds(new Set(limited.map(b => Number(b.image_relation_id))));

			// Fetch fresh version and save
			try {
				const versions = await getCacheVersions();
				versionRef.current = versions?.bookmarks ?? versionRef.current + 1;
			} catch {
				versionRef.current += 1;
			}
			cacheStore.set(CACHE_KEY, limited, versionRef.current);
			initializedRef.current = true;
		} catch (e) {
			setError(e);
		} finally {
			setLoading(false);
		}
	}, [user]);

	const value = useMemo(() => ({
		bookmarks,
		loading,
		error,
		isBookmarked,
		addBookmark,
		removeBookmark,
		toggleBookmark,
		reloadBookmarks,
		MAX_BOOKMARKS_TO_CACHE,
	}), [bookmarks, loading, error, isBookmarked, addBookmark, removeBookmark, toggleBookmark, reloadBookmarks]);

	return (
		<BookmarksContext.Provider value={value}>
			{children}
		</BookmarksContext.Provider>
	);
};

export const useBookmarks = () => {
	const ctx = useContext(BookmarksContext);
	if (!ctx) throw new Error('useBookmarks must be used within a BookmarksProvider');
	return ctx;
};
